import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, ImagePlus, X, Eye, RotateCcw, Check,
  ChevronDown, Sparkles, ExternalLink, Monitor, Smartphone, FolderOpen,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import api, { mediaUrl } from '../api';
import MediaPicker from '../components/MediaPicker';

export default function PageEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const [device, setDevice] = useState('desktop');
  const iframeRef = useRef(null);

  // Load page
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/pages/${slug}`);
        setPage(data);
        setOriginal(JSON.parse(JSON.stringify(data)));
        const opens = {};
        (data.sections || []).forEach((s, i) => { opens[s.id] = i === 0; });
        setOpenSections(opens);
      } catch (e) {
        toast.error('Failed to load page');
        navigate('/admin/pages');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, navigate]);

  // Push live preview updates to iframe via postMessage
  const sendPreview = (target) => {
    if (!page) return;
    const iframe = target || iframeRef.current;
    try {
      iframe?.contentWindow?.postMessage(
        { type: 'gems-cms-preview', slug: page.slug, sections: page.sections },
        '*'
      );
    } catch (e) { /* ignore */ }
  };

  // Send updates whenever content changes
  useEffect(() => {
    if (!page || !iframeRef.current) return;
    const t = setTimeout(() => sendPreview(), 150);
    return () => clearTimeout(t);
  }, [page]);

  // Listen for "ready" handshake from iframe and re-send latest content
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'gems-cms-preview-ready') {
        sendPreview();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
     
  }, [page]);

  const dirty = useMemo(() => {
    if (!page || !original) return false;
    return JSON.stringify(page.sections) !== JSON.stringify(original.sections);
  }, [page, original]);

  const updateField = (sectionId, fieldKey, value) => {
    setPage((p) => ({
      ...p,
      sections: p.sections.map((s) =>
        s.id !== sectionId
          ? s
          : { ...s, fields: s.fields.map((f) => (f.key === fieldKey ? { ...f, value } : f)) }
      ),
    }));
  };

  const handleSave = async () => {
    if (!page) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/pages/${page.slug}`, { sections: page.sections });
      setPage(data);
      setOriginal(JSON.parse(JSON.stringify(data)));
      toast.success('Saved!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!original) return;
    setPage(JSON.parse(JSON.stringify(original)));
    toast.message('Reverted unsaved changes');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-white/50">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading page editor…
      </div>
    );
  }
  if (!page) return null;

  const previewSrc = `${page.route || '/'}${page.route?.includes('?') ? '&' : '?'}cms_preview=1`;

  return (
    <div className="-m-7">
      {/* Top bar */}
      <div className="sticky top-[64px] z-20 bg-[#0f0d12]/95 backdrop-blur-sm border-b border-white/[0.06] px-7 py-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/admin/pages" className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#5DCAB5] font-semibold">Editing</div>
            <div className="font-serif-display text-[22px] font-bold truncate">{page.title}</div>
          </div>
          {dirty && (
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#FCD968] bg-[#FCD968]/15 px-2.5 py-1 rounded-full">
              <span className="h-1.5 w-1.5 rounded-full bg-[#FCD968]" /> Unsaved changes
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={handleReset}
            disabled={!dirty || saving}
            className="h-10 rounded-full text-white/65 hover:bg-white/10 hover:text-white"
          >
            <RotateCcw className="h-4 w-4 mr-1.5" /> Revert
          </Button>
          <a
            href={page.route || '/'} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white/5 border border-white/10 text-[13px] font-semibold hover:bg-white/10 transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" /> View live
          </a>
          <Button
            onClick={handleSave}
            disabled={!dirty || saving}
            className="h-10 rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold px-5 disabled:opacity-50 btn-press shadow-[0_8px_20px_-6px_rgba(229,97,76,0.5)]"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save Changes</>}
          </Button>
        </div>
      </div>

      {/* Split layout */}
      <div className="grid lg:grid-cols-[minmax(0,520px)_minmax(0,1fr)] gap-0 min-h-[calc(100vh-64px-72px)]">
        {/* Editor panel */}
        <div className="border-r border-white/[0.06] p-7 space-y-4 overflow-y-auto">
          <div className="text-[12px] text-white/45">
            <Sparkles className="h-3 w-3 inline mr-1.5 text-[#E5614C]" />
            Edits update the live preview instantly. Click <span className="text-white font-semibold">Save Changes</span> to publish.
          </div>

          {page.sections.map((section) => {
            const open = openSections[section.id];
            return (
              <div key={section.id} className="rounded-2xl bg-[#16131a] border border-white/[0.06] overflow-hidden">
                <button
                  onClick={() => setOpenSections((o) => ({ ...o, [section.id]: !o[section.id] }))}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.03] transition-colors"
                >
                  <div className="text-left">
                    <div className="font-serif-display font-bold text-[17px]">{section.label}</div>
                    <div className="text-[11px] text-white/45 uppercase tracking-[0.14em] mt-0.5">{section.fields?.length || 0} fields</div>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-white/50 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                  <div className="px-5 pb-5 space-y-4 border-t border-white/[0.04] pt-4">
                    {section.fields.map((field) => (
                      <FieldEditor
                        key={field.key}
                        field={field}
                        onChange={(v) => updateField(section.id, field.key, v)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Preview panel */}
        <div className="bg-[#0a0810] p-5 lg:p-7 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-[12px] text-white/55">
              <Eye className="h-3.5 w-3.5" />
              <span className="font-semibold text-white/75">Live Preview</span>
              <span className="text-white/35">— updates as you type</span>
            </div>
            <div className="flex gap-1 bg-[#16131a] p-1 rounded-full border border-white/[0.06]">
              <button
                onClick={() => setDevice('desktop')}
                className={`px-3 h-8 rounded-full flex items-center gap-1.5 text-[12px] font-semibold transition-colors ${
                  device === 'desktop' ? 'bg-white text-[#0f0d12]' : 'text-white/55 hover:text-white'
                }`}
              >
                <Monitor className="h-3.5 w-3.5" /> Desktop
              </button>
              <button
                onClick={() => setDevice('mobile')}
                className={`px-3 h-8 rounded-full flex items-center gap-1.5 text-[12px] font-semibold transition-colors ${
                  device === 'mobile' ? 'bg-white text-[#0f0d12]' : 'text-white/55 hover:text-white'
                }`}
              >
                <Smartphone className="h-3.5 w-3.5" /> Mobile
              </button>
            </div>
          </div>

          <div className="flex-1 rounded-2xl bg-white overflow-hidden shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)] flex items-stretch justify-center">
            <iframe
              ref={iframeRef}
              src={previewSrc}
              title="Live preview"
              onLoad={(e) => sendPreview(e.currentTarget)}
              className={`bg-white transition-[width,max-width] duration-300 ${
                device === 'mobile' ? 'w-[390px] max-w-full' : 'w-full'
              } h-full min-h-[640px]`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldEditor({ field, onChange }) {
  const isImage = field.type === 'image';
  const isTextarea = field.type === 'textarea';
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/media/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-white/75 text-[12px] font-semibold uppercase tracking-[0.12em]">
        {field.label}
      </Label>

      {isImage ? (
        <div className="space-y-2">
          {field.value && (
            <div className="relative rounded-xl overflow-hidden bg-[#0f0d12] border border-white/[0.06] aspect-[16/10] group">
              <img src={mediaUrl(field.value)} alt={field.label} className="w-full h-full object-cover" />
              <button
                onClick={() => onChange('')}
                className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                title="Clear image"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="h-10 rounded-lg bg-white/5 hover:bg-white/10 text-white font-semibold flex-1 border border-white/10"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ImagePlus className="h-4 w-4 mr-1.5" /> Upload</>}
            </Button>
            <Button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="h-10 rounded-lg bg-white/5 hover:bg-white/10 text-white font-semibold flex-1 border border-white/10"
            >
              <FolderOpen className="h-4 w-4 mr-1.5" /> Library
            </Button>
            <input
              ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden"
            />
          </div>
          <Input
            value={field.value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Or paste an image URL"
            className="h-10 rounded-lg bg-[#0f0d12] border-white/10 text-white placeholder:text-white/30 text-[12px] focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
          />
          <MediaPicker
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onSelect={(item) => onChange(item.url)}
            title={`Pick image for "${field.label}"`}
          />
        </div>
      ) : isTextarea ? (
        <Textarea
          rows={4}
          value={field.value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-lg bg-[#0f0d12] border-white/10 text-white text-[14px] resize-none focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C] leading-relaxed"
        />
      ) : (
        <Input
          value={field.value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 rounded-lg bg-[#0f0d12] border-white/10 text-white text-[14px] focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
        />
      )}
    </div>
  );
}
